import React from "react"

import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from "@coval/ui"

interface RenameDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Runs the rename. The dialog does not close itself; the caller closes it. */
  onConfirm: () => void
  /** Dialog heading, e.g. "Rename Agent". */
  title: string
  /** Field label, e.g. "Agent name". */
  label: string
  value: string
  onValueChange: (value: string) => void
  /** Blocks dismissal and shows the confirm button's loading state. */
  isRenaming?: boolean
  /** Rendered below the field, e.g. a name collision the backend rejected. */
  error?: React.ReactNode
  confirmTestId?: string
}

/**
 * Shared rename dialog: one named text field, Enter to confirm, and a Save
 * button that stays disabled until the field holds something other than
 * whitespace. Presentational: the caller owns the value, the rename call, and closing the dialog on success.
 */
export function RenameDialog({
  open,
  onOpenChange,
  onConfirm,
  title,
  label,
  value,
  onValueChange,
  isRenaming = false,
  error,
  confirmTestId,
}: RenameDialogProps) {
  const inputId = React.useId()
  const canConfirm = value.trim().length > 0 && !isRenaming

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!isRenaming) {
          onOpenChange(next)
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={inputId}>{label}</Label>
            <Input
              id={inputId}
              value={value}
              placeholder="Enter a name..."
              disabled={isRenaming}
              onChange={(event) => onValueChange(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && canConfirm) {
                  event.preventDefault()
                  onConfirm()
                }
              }}
              autoFocus
            />
            {error ? (
              <p role="alert" className="text-xs text-coval-danger-text">
                {error}
              </p>
            ) : null}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={isRenaming}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={onConfirm}
            loading={isRenaming}
            disabled={!canConfirm}
            data-testid={confirmTestId}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
